import { useSelector } from "react-redux";
import { selectIsOnline } from "../../../store/slices/network";
import {
  useGetFormIndexQuery,
  useGetSingleFormQuery,
} from "../../../services/api/formsApi";
import { useMemo } from "react";
import { selectFormById, selectFormsByTag } from "../../../store/slices/forms";
import { FormEnums } from "../../../services/api/types/forms/enums/FormEnums";
import { RootState } from "../../../services/store";

interface Props {
  tag?: FormEnums;
  formId?: number;
}

export function useFormsWithOffline({ tag, formId }: Props) {
  const isOnline = useSelector(selectIsOnline);

  const offlineData = useSelector((state: RootState) =>
    tag
      ? selectFormsByTag(state, tag)
      : formId
        ? selectFormById(state, formId)
        : null,
  );

  const {
    data: onlineDataTag,
    isFetching: isFetchingTag,
    isError: isErrorTag,
  } = useGetFormIndexQuery(
    {
      filter: {
        tag: tag!,
      },
    },
    { skip: !tag },
  );

  const {
    data: onlineDataForm,
    isFetching: isFetchingForm,
    isError: isErrorForm,
  } = useGetSingleFormQuery({ formId: formId! }, { skip: !formId });

  const isFetching = isFetchingTag || isFetchingForm;
  const isError = isErrorTag || isErrorForm;
  const onlineData = tag
    ? (onlineDataTag ?? [])
    : onlineDataForm
      ? [onlineDataForm]
      : [];

  const data = useMemo(() => {
    if (isFetching || (isOnline && onlineData)) return onlineData;
    if (isError && offlineData)
      return Array.isArray(offlineData) ? offlineData : [offlineData];
    return onlineData;
  }, [onlineData, offlineData, isOnline, isFetching, isError]);

  return {
    isFetching,
    data,
  };
}
